import styled from '@emotion/styled'
import { useHover } from '@react-aria/interactions'
import { AnimatePresence, m } from 'framer-motion'
import { useRef } from 'react'
import Confetti from 'react-dom-confetti'

import { Tooltip } from 'layouts/components/SharedStyledComponents'
import { rotation } from 'styles/animations'

type Props = {
	children: React.ReactNode
	isVisible: boolean
}

const Wrapper = styled.div`
	position: relative;
`

const BoostedConfetti = styled(Confetti)<{ top: number; left: number }>`
	position: fixed !important;
	top: ${({ top }) => `${top}px`};
	left: ${({ left }) => `${left}px`};
`

const Container = styled.div`
	position: relative;
	overflow: hidden;
	border-radius: ${({ theme }) => theme.borderRadiuses.lg};
	z-index: 1;
`

const Gradient = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	width: 100px;
	height: 100px;
	background: conic-gradient(from 0deg, #994bf4, #ff3f00, #ffd000, #00d969, #0246ff, #9a4cf5);
	border-radius: ${({ theme }) => theme.borderRadiuses.round};
	animation: ${rotation} 1.5s linear infinite;
	translate: -50% -50%;
`

const StyledTooltip = styled(Tooltip)`
	top: 4px;
	right: 40px;
	padding: ${({ theme }) => theme.spaces.space2};

	white-space: pre;
`

function EnhancementButtonLoader({ children, isVisible }: Props) {
	const { hoverProps, isHovered } = useHover({})
	const ref = useRef<HTMLDivElement>(null)
	const confettiPosition = ref.current?.getBoundingClientRect()
	return (
		<Wrapper ref={ref}>
			<Container {...hoverProps}>
				<AnimatePresence>
					{isVisible && (
						<m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
							<Gradient />
						</m.div>
					)}
				</AnimatePresence>
				<m.div
					animate={isVisible ? 'loading' : 'stale'}
					variants={{
						stale: { scale: 1 },
						loading: { scale: 0.8 },
					}}
				>
					{children}
				</m.div>
			</Container>
			<BoostedConfetti
				top={confettiPosition?.top ?? 0}
				left={confettiPosition?.left ?? 0}
				active={!isVisible}
				config={{ spread: 360, startVelocity: 15, elementCount: 20, duration: 1000 }}
			/>
			{!isVisible && (
				<AnimatePresence>
					{isHovered && (
						<StyledTooltip
							initial={{ opacity: 0, right: 36 }}
							animate={{ opacity: 1, right: 40 }}
							exit={{ opacity: 0, right: 36 }}
							transition={{ duration: 0.3, ease: [0.39, 0.54, 0.12, 0.94] }}
						>
							AI enhance
						</StyledTooltip>
					)}
				</AnimatePresence>
			)}
		</Wrapper>
	)
}

export default EnhancementButtonLoader
